// =============================================================================
// What we accept as an uploaded image (spec §6 flow 2, phase 8).
//
// The type is decided by the FILE'S OWN BYTES, never by what the client said.
// This is not pedantry — the R2 bucket is public by necessity (Instagram and
// Threads fetch `image_url` from their own servers), it is served from
// media.cart10.com, and we set the stored object's Content-Type from this
// decision. Trusting the multipart header would let anyone put a chosen
// Content-Type on a chosen byte string on a cart10.com origin.
//
// Pure and dependency-free so it can be tested directly and used from either
// app. Uint8Array rather than Buffer: the web package has no Node types.
// =============================================================================
/** The only types we store. Anything else is refused, not converted. */
export const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
/**
 * 10 MB. Phone cameras produce 2–6 MB, so this is generous for the real case
 * while bounding what a single request can push into the bucket. It also sits
 * under the image API's own input ceiling, so an accepted upload cannot fail
 * later for being too big to enhance.
 */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/** Smaller than any real photo; catches truncated and empty uploads. */
export const MIN_IMAGE_BYTES = 100;
function startsWith(bytes, prefix, offset = 0) {
    if (bytes.length < offset + prefix.length)
        return false;
    return prefix.every((byte, i) => bytes[offset + i] === byte);
}
const SIGNATURES = [
    // 89 50 4E 47 0D 0A 1A 0A
    { type: "image/png", match: (b) => startsWith(b, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]) },
    // FF D8 FF — every JPEG variant (JFIF, Exif, raw) shares this.
    { type: "image/jpeg", match: (b) => startsWith(b, [0xff, 0xd8, 0xff]) },
    // "RIFF" ....  "WEBP" — the size field sits between the two markers.
    {
        type: "image/webp",
        match: (b) => startsWith(b, [0x52, 0x49, 0x46, 0x46]) && startsWith(b, [0x57, 0x45, 0x42, 0x50], 8),
    },
];
/**
 * The real type of these bytes, or null if it is not one we accept.
 *
 * Note what is deliberately absent: SVG and GIF. SVG is a document that can
 * carry script and event handlers, and serving one from media.cart10.com
 * would be script execution on a cart10.com origin — so it is refused rather
 * than sanitised. GIF is refused because the enhancement and publish paths
 * both assume a still frame.
 */
export function sniffImageType(bytes) {
    return SIGNATURES.find((s) => s.match(bytes))?.type ?? null;
}
/**
 * Size alone, from a byte count.
 *
 * Split out so the browser can judge a `File` by its `size` without reading
 * it: a 40 MB photo should be refused before it is loaded into memory, let
 * alone uploaded, and the message should name the real size rather than
 * repeating the limit back.
 */
export function checkImageSize(byteLength) {
    if (byteLength === 0) {
        return { reason: "empty", message: "That file is empty — pick the photo again." };
    }
    if (byteLength < MIN_IMAGE_BYTES) {
        return {
            reason: "too_small",
            message: "That file is too small to be a photo — it may not have finished uploading.",
        };
    }
    if (byteLength > MAX_IMAGE_BYTES) {
        const mb = (byteLength / (1024 * 1024)).toFixed(1);
        return {
            reason: "too_large",
            message: `That photo is ${mb} MB. The limit is ${MAX_IMAGE_BYTES / (1024 * 1024)} MB — your phone's camera settings can usually be set lower.`,
        };
    }
    return null;
}
/**
 * Decides whether these bytes may be stored, and as what.
 *
 * Messages are written for the person who picked the file, not for a log: a
 * phone's screenshot of a receipt is a real thing people try to post, and
 * "Unsupported file type" tells them nothing about what to do instead.
 */
export function checkImageUpload(bytes) {
    const sizeProblem = checkImageSize(bytes.length);
    if (sizeProblem)
        return { ok: false, ...sizeProblem };
    const contentType = sniffImageType(bytes);
    if (!contentType) {
        return {
            ok: false,
            reason: "unsupported_type",
            message: "Use a JPG, PNG or WebP photo. PDFs, screenshots saved as SVG and HEIC aren't supported yet.",
        };
    }
    return { ok: true, contentType };
}
/** File extension for a stored object, matching clients/r2.ts. */
export function extensionForImage(type) {
    switch (type) {
        case "image/png":
            return ".png";
        case "image/jpeg":
            return ".jpg";
        case "image/webp":
            return ".webp";
    }
}
//# sourceMappingURL=imageTypes.js.map